import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Loader2, Trash2 } from "lucide-react";

const SLOTS = ["Traseira", "Frente", "Lado esquerdo", "Lado direito", "Interior"];

const loadImageEl = (file) => new Promise((res, rej) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { res({ img, revoke: () => URL.revokeObjectURL(url) }); };
  img.onerror = () => { URL.revokeObjectURL(url); rej(new Error("decode")); };
  img.src = url;
});

const toJpeg = async (file) => {
  if (!file || (!file.type.startsWith("image/") && !/\.(heic|heif|jpe?g|png|webp)$/i.test(file.name || ""))) { return file; }
  let bmp = null; let imgEl = null; let revoke = null;
  try { bmp = await createImageBitmap(file); } catch {
    try { ({ img: imgEl, revoke } = await loadImageEl(file)); } catch { return file; }
  }
  const w = bmp?.width || imgEl?.naturalWidth || 0;
  const h = bmp?.height || imgEl?.naturalHeight || 0;
  if (!w || !h) { revoke?.(); return file; }
  const scale = Math.min(1, 1600 / Math.max(w, h));
  const canvas = Object.assign(document.createElement("canvas"), { width: Math.round(w * scale), height: Math.round(h * scale) });
  canvas.getContext("2d").drawImage(bmp || imgEl, 0, 0, canvas.width, canvas.height);
  bmp?.close?.(); revoke?.();
  const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.82));
  canvas.width = 0; canvas.height = 0;
  if (!blob) { return file; }
  return new File([blob], (file.name || "foto").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
};

function SlotThumb({ photo, alt }) {
  const [url, setUrl] = useState(photo.localUrl || "");
  useEffect(() => {
    if (photo.localUrl) { setUrl(photo.localUrl); return; }
    let revoke = null;
    (async () => {
      try {
        const resp = await api.get(`/files/${photo.id}`, { responseType: "blob" });
        const u = URL.createObjectURL(resp.data);
        revoke = u;
        setUrl(u);
      } catch {}
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [photo.id, photo.localUrl]);
  if (!url) {
    return <div data-testid={`intake-thumb-loading-${alt}`} className="w-full h-32 flex items-center justify-center rounded-md bg-zinc-900 border border-zinc-800"><Loader2 className="h-5 w-5 text-zinc-600 animate-spin" /></div>;
  }
  return <img data-testid={`intake-thumb-${alt}`} src={url} alt={alt} className="w-full h-32 object-cover rounded-md" />;
}

export function IntakePhotoSlots({ order, setOrder, testid = "intake-slots", baseUrl = "" }) {
  const [busy, setBusy] = useState(null);
  const refs = useRef({});
  const photos = order.intake_photos || [];
  const upload = async (slot, file) => {
    if (!file) { return; }
    setBusy(slot);
    try {
      const jpeg = await toJpeg(file);
      const fd = new FormData();
      fd.append("file", jpeg);
      const { data } = await api.post(`${baseUrl}/orders/${order.id}/photos?category=intake&slot=${encodeURIComponent(slot)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      data.localUrl = URL.createObjectURL(jpeg);
      setOrder((o) => ({ ...o, intake_photos: [...(o.intake_photos || []), data] }));
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };
  const remove = async (p) => {
    if (!window.confirm("Remover esta foto?")) { return; }
    try {
      if (p.localUrl) { URL.revokeObjectURL(p.localUrl); }
      await api.delete(`${baseUrl}/orders/${order.id}/photos/${p.id}`);
      setOrder((o) => ({ ...o, intake_photos: (o.intake_photos || []).filter((x) => x.id !== p.id) }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid={testid} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {SLOTS.map((slot, idx) => {
        const p = photos.find((x) => x.slot === slot) || photos[idx];
        return (
          <div key={slot} className="rounded-xl border border-zinc-800 bg-zinc-950/50 p-2">
            <div className="flex items-center justify-between gap-2 mb-2 text-xs font-bold uppercase text-zinc-400"><span>{slot}</span>{p ? <button type="button" data-testid={`intake-remove-${slot}`} onClick={() => remove(p)} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button> : null}</div>
            {p ? (
              <SlotThumb photo={p} alt={slot} />
            ) : (
              <label className="h-32 w-full flex items-center justify-center rounded-md border border-dashed border-zinc-700 text-zinc-400 text-xs cursor-pointer hover:border-green-brand">
                {busy === slot ? <Loader2 className="h-5 w-5 animate-spin" /> : <span className="flex items-center gap-1.5"><Camera className="h-4 w-4" /> Enviar foto (opcional)</span>}
                <input ref={(el) => (refs.current[slot] = el)} type="file" accept="image/*,.heic,.heif" capture="environment" className="hidden" disabled={!!busy}
                  data-testid={`intake-input-${slot}`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { upload(slot, f); } }} />
              </label>
            )}
          </div>
        );
      })}
    </div>
  );
}
